import React, { useEffect, useId, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Settings, UserPlus } from 'lucide-react';
import { useNotificationPolling, useNotifications } from '../../hooks/useNotifications';
import {
  getNotificationDestination,
  formatAbsoluteTime,
  formatUnreadCount,
} from '../../utils/notifications';
import { connectionsService, type ConnectionWithUser } from '../../api/services/connections.service';
import { ConnectionRequestItem } from '../../components/connections';
import { timeAgoShort } from '../../components/network/timeAgo';
import { Alert, Skeleton, cn } from '../../components/ui';
import { NotificationIcon } from './NotificationIcon';
import { getNotificationBody, getNotificationTitle } from './notificationMeta';

interface NotificationBellProps {
  variant?: 'default' | 'icon';
}

type BellTab = 'notifications' | 'connections';

const ListSkeleton: React.FC = () => (
  <div aria-busy="true" className="space-y-4 px-4 py-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-start gap-3">
        <Skeleton className="size-9 rounded-lg" />
        <div className="flex-1 space-y-2 pt-0.5">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3 w-full" />
        </div>
      </div>
    ))}
  </div>
);

const PanelEmpty: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({ icon, title, body }) => (
  <div className="flex flex-col items-center px-6 py-10 text-center">
    <span className="flex size-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 [&_svg]:size-5">
      {icon}
    </span>
    <p className="mt-3 text-sm font-semibold text-gray-900">{title}</p>
    <p className="mt-1 max-w-[240px] text-meta leading-relaxed text-gray-500">{body}</p>
  </div>
);

const NotificationBell: React.FC<NotificationBellProps> = ({ variant = 'default' }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<BellTab>('notifications');
  const tabIdBase = useId();
  const tabId = (value: BellTab) => `${tabIdBase}-tab-${value}`;
  const panelId = `${tabIdBase}-panel`;
  const [pendingConnections, setPendingConnections] = useState<ConnectionWithUser[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Polling for unread count (every 30 seconds)
  const { unreadCount, refetch: refetchUnreadCount } = useNotificationPolling({
    interval: 30000,
    enabled: true,
  });

  // Recent notifications, fetched when the dropdown opens
  const {
    notifications,
    isLoading,
    error: notificationsError,
    refetch: refetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications({
    limit: 10,
    autoFetch: false,
  });

  useEffect(() => {
    if (open) {
      refetchNotifications();
    }
  }, [open, refetchNotifications]);

  const fetchPendingConnections = useCallback(async () => {
    setIsLoadingConnections(true);
    setConnectionsError(null);
    try {
      const connections = await connectionsService.getPendingConnections();
      setPendingConnections(connections);
    } catch (error) {
      console.error('Failed to fetch pending connections:', error);
      setConnectionsError("Couldn't load connection requests.");
    } finally {
      setIsLoadingConnections(false);
    }
  }, []);

  // Load requests whenever the panel opens (not only on the Connections tab) so the tab count is right
  useEffect(() => {
    if (open) {
      fetchPendingConnections();
    }
  }, [open, fetchPendingConnections]);

  const syncNetworkQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['connections'] });
  };

  const handleAcceptConnection = async (connectionId: string) => {
    setConnectionsError(null);
    try {
      await connectionsService.acceptConnection(connectionId);
      setPendingConnections((prev) => prev.filter((c) => c.id !== connectionId));
      syncNetworkQueries();
    } catch (error) {
      console.error('Failed to accept connection:', error);
      setConnectionsError("Couldn't accept that request. Please try again.");
    }
  };

  const handleDeclineConnection = async (connectionId: string) => {
    setConnectionsError(null);
    try {
      await connectionsService.declineConnection(connectionId);
      setPendingConnections((prev) => prev.filter((c) => c.id !== connectionId));
      syncNetworkQueries();
    } catch (error) {
      console.error('Failed to decline connection:', error);
      setConnectionsError("Couldn't decline that request. Please try again.");
    }
  };

  // Click outside and escape key handlers
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const handleNotificationClick = useCallback(
    async (notification: (typeof notifications)[0]) => {
      setOpen(false);

      if (!notification.isRead) {
        await markAsRead(notification.id);
        refetchUnreadCount();
      }

      navigate(getNotificationDestination(notification));
    },
    [markAsRead, navigate, refetchUnreadCount],
  );

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    refetchUnreadCount();
  };

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const totalCount = unreadCount + pendingConnections.length;
  const hasUnread = totalCount > 0;
  const badgeText = formatUnreadCount(totalCount);
  const hasUnreadInList = notifications.some((n) => !n.isRead);

  const tabs: { value: BellTab; label: string; count: number }[] = [
    { value: 'notifications', label: 'Updates', count: unreadCount },
    { value: 'connections', label: 'Requests', count: pendingConnections.length },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={hasUnread ? `Notifications, ${badgeText} unread` : 'Notifications'}
        className={
          variant === 'icon'
            ? 'relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900'
            : 'relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-700 ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50'
        }
      >
        <Bell className={variant === 'icon' ? 'size-[18px]' : 'size-5'} aria-hidden />

        {variant !== 'icon' && hasUnread && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-600 px-1 text-xs font-semibold leading-none text-white ring-2 ring-white">
            {badgeText}
          </span>
        )}
        {variant === 'icon' && hasUnread && (
          <span className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-600 px-1 text-xs font-semibold leading-none text-white ring-2 ring-white">
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Recent notifications"
          className="fixed inset-x-2 top-[68px] z-50 flex max-h-[calc(100vh-84px)] origin-top-right animate-dialog-in flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-950/5 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[400px] sm:max-h-[min(640px,calc(100vh-96px))]"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-3.5">
            <h2 className="text-sm font-semibold tracking-tight text-gray-900">Notifications</h2>
            <div className="flex items-center gap-1">
              {activeTab === 'notifications' && hasUnreadInList && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="rounded-md px-2 py-1 text-meta font-medium text-brand-700 transition-colors hover:bg-brand-50"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => goTo('/profile/settings/notifications')}
                aria-label="Notification settings"
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <Settings className="size-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div
            role="tablist"
            aria-label="Notification type"
            className="flex gap-5 border-b border-gray-100 px-4"
            onKeyDown={(e) => {
              if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
              e.preventDefault();
              const index = tabs.findIndex((t) => t.value === activeTab);
              const nextIndex =
                e.key === 'Home'
                  ? 0
                  : e.key === 'End'
                    ? tabs.length - 1
                    : (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
              const next = tabs[nextIndex].value;
              setActiveTab(next);
              document.getElementById(tabId(next))?.focus();
            }}
          >
            {tabs.map((tab) => {
              const active = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  id={tabId(tab.value)}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={panelId}
                  tabIndex={active ? 0 : -1}
                  onClick={() => setActiveTab(tab.value)}
                  className={cn(
                    'relative inline-flex h-10 items-center gap-1.5 text-meta font-medium transition-colors',
                    active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800',
                  )}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span
                      className={cn(
                        'tabular rounded-full px-1.5 py-px text-xs font-semibold',
                        active ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600',
                      )}
                    >
                      {formatUnreadCount(tab.count)}
                    </span>
                  )}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-x-0 -bottom-px h-0.5 rounded-full',
                      active ? 'bg-brand-600' : 'bg-transparent',
                    )}
                  />
                </button>
              );
            })}
          </div>

          {/* Tab content: one panel whose content and label follow the selected tab */}
          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId(activeTab)}
            className="scrollbar-thin min-h-0 flex-1 overflow-y-auto"
          >
            {activeTab === 'notifications' ? (
              notificationsError && notifications.length === 0 ? (
                <div className="p-4">
                  <Alert
                    variant="error"
                    description="Couldn't load notifications."
                    action={
                      <button
                        type="button"
                        onClick={() => refetchNotifications()}
                        className="text-meta font-semibold underline underline-offset-2"
                      >
                        Try again
                      </button>
                    }
                  />
                </div>
              ) : isLoading && notifications.length === 0 ? (
                <ListSkeleton />
              ) : notifications.length === 0 ? (
                <PanelEmpty
                  icon={<BellOff />}
                  title="You're all caught up"
                  body="Buy box matches, bids and messages will show up here."
                />
              ) : (
                <ul className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <li key={notification.id}>
                      <button
                        type="button"
                        onClick={() => handleNotificationClick(notification)}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus-visible:bg-gray-50"
                      >
                        <NotificationIcon type={notification.type} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'line-clamp-1 text-sm leading-snug text-gray-900',
                              notification.isRead ? 'font-medium' : 'font-semibold',
                            )}
                          >
                            {getNotificationTitle(notification)}
                          </span>
                          <span className="mt-0.5 line-clamp-2 block text-meta leading-snug text-gray-500">
                            {getNotificationBody(notification)}
                          </span>
                          <span
                            className="tabular mt-1 block text-xs text-gray-400"
                            title={formatAbsoluteTime(notification.createdAt)}
                          >
                            {timeAgoShort(notification.createdAt)}
                          </span>
                        </span>
                        {!notification.isRead && (
                          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-500">
                            <span className="sr-only">Unread</span>
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <>
                {connectionsError && (
                  <div className="p-4 pb-0">
                    <Alert variant="error" description={connectionsError} />
                  </div>
                )}
                {isLoadingConnections && pendingConnections.length === 0 ? (
                  <ListSkeleton />
                ) : pendingConnections.length === 0 ? (
                  !connectionsError && (
                    <PanelEmpty
                      icon={<UserPlus />}
                      title="No pending requests"
                      body="When someone asks to join your network, you can accept them here."
                    />
                  )
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pendingConnections.map((connection) => (
                      <ConnectionRequestItem
                        key={connection.id}
                        connection={connection}
                        onAccept={handleAcceptConnection}
                        onDecline={handleDeclineConnection}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 p-1.5">
            <button
              type="button"
              onClick={() =>
                goTo(activeTab === 'connections' ? '/user/network' : '/user/notifications')
              }
              className="group flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-meta font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
            >
              {activeTab === 'connections' ? 'Open my network' : 'View all notifications'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
